import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  HomePageTitle,
  HomePageWrapper,
  HomeContentColumn,
  TabsContentWrapper,
} from "../Home/HomeStyled";
import { fetchVeteransAffairsList } from "../../slice/HomeSlice";
import { checkSession } from "../../slice/AuthSlice";
import ErrorToast from "../../components/Notification/ErrorToast";
import Loader from "../../components/Loader/Loader";
import VeteransAffairsTabContainer from "../Home/VeteransAffairs";
import { getRowProps, handleErrorFactory, handleAPICalls } from "../Home/HomeUtils";

/**
 * VA E-Submission, as its own page behind a left-sidebar icon
 * (Matthew, 2026-08-28: "maybe VA E-Submission moves to an icon in the
 * lefthand column — to free up this screen to be a real work tool").
 *
 * This is a submission console — a place you go to push filings to the
 * VA and watch their statuses — not part of anyone's daily work stack,
 * so it no longer occupies a Home tab. Same container component as
 * before (websocket status refresh, evidence modal, error/pending
 * chips), just hosted at /va-submissions with its own fetch lifecycle.
 */
const VaSubmissionsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [activeTab, setActiveTab] = useState("veteransAffairs");

  const { veteransAffairsList } = useSelector((state) => state.home);
  const { sessionChecked } = useSelector((state) => state.auth);

  const handleError = handleErrorFactory(setIsLoading, setError, setShowNotification);
  const callAPICalls = handleAPICalls(dispatch, setIsLoading, {
    fetchVeteransAffairsList,
  });

  useEffect(() => {
    if (!sessionChecked) {
      dispatch(checkSession());
    }
    const loadInitialData = async () => {
      setIsInitialLoad(true);
      try {
        await callAPICalls("veteransAffairs");
      } catch (err) {
        handleError(err?.message || "Failed to load VA submissions");
      } finally {
        setIsInitialLoad(false);
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionChecked]);

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    await callAPICalls(tab);
  };

  const rowProps = getRowProps(navigate, dispatch, handleError);

  return (
    <HomePageWrapper>
      {isInitialLoad && isLoading && <Loader />}
      <HomeContentColumn>
        <HomePageTitle>VA E-Submission</HomePageTitle>
      </HomeContentColumn>
      <TabsContentWrapper>
        <VeteransAffairsTabContainer
          activeTab={activeTab}
          handleTabChange={handleTabChange}
          isLoading={isLoading}
          isInitialLoad={isInitialLoad}
          veteransAffairsList={veteransAffairsList}
          getRowProps={rowProps}
          setError={setError}
          setShowNotification={setShowNotification}
          handleError={handleError}
          setIsLoading={setIsLoading}
          handleAPICalls={callAPICalls}
        />
      </TabsContentWrapper>
      {showNotification && (
        <ErrorToast
          message={error}
          onClose={(id) =>
            setError((prev) => prev.filter((msg) => msg.id !== id))
          }
        />
      )}
    </HomePageWrapper>
  );
};

export default VaSubmissionsPage;
